/**
 * React 19.2 logs every render of a component in profile mode, and that logging
 * walks the component's props. A tRPC client is a recursive Proxy that turns any
 * property access into a procedure path, so when the walker reaches
 * `client.name` and builds a string out of it, the proxy is called as a
 * function and throws "undefined is not a function" — inside React's commit
 * phase, which leaves the work loop broken ("Should not already be working.")
 * and the app renders nothing.
 *
 * Wrapping the client answers the handful of keys the walker uses with plain
 * values, and passes everything else straight through.
 */
const describedName = 'TRPCClient';

export function makeClientDescribable<T extends object>(client: T): T {
  return new Proxy(client, {
    get(target, property, receiver) {
      // Only stand in for keys the walker uses. A router that ever names a
      // procedure `name`, `toString` or `valueOf` keeps working, because a
      // recursive tRPC proxy answers every key and this check cannot tell them
      // apart — so the override applies to the four keys and nothing else.
      switch (property) {
        case 'name':
          return describedName;
        case 'toString':
        case 'valueOf':
          return () => `[${describedName}]`;
        case Symbol.toPrimitive:
          return () => `[${describedName}]`;
        case Symbol.toStringTag:
          return describedName;
        default:
          return Reflect.get(target, property, receiver);
      }
    },
  });
}
